import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import "./AlertFormEdit.css";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Toast } from "../../../helpers/alert";
import useFilteredUsersByDevice from "../../../hooks/useFilteredUsersByDevice";
import { getCostUnitPreview } from "../../../redux/apiCalls/alertsCall/alertsCall";

const CostAlertFormEdit = ({
  devices,
  users,
  onCancel,
  onSave,
  initialData,
  userPrivilege,
}) => {
  const intl = useIntl();
  const user = useSelector((state) => state.user.currentUser);
  const token = user?.accessToken || "";

  const services = useMemo(
    () => [
      { value: "electricidad", label: "Electricidad" },
      { value: "agua", label: "Agua" },
      { value: "gas", label: "Gas" },
    ],
    []
  );

  const intervals = useMemo(
    () => [
      { value: "d", label: "día" },
      { value: "s", label: "semana" },
      { value: "m", label: "mes" },
    ],
    []
  );

  const operators = useMemo(
    () => [
      { value: ">", label: ">" },
      { value: "<", label: "<" },
      { value: "=", label: "=" },
    ],
    []
  );

  const daysOptions = useMemo(
    () => [
      { value: "lunes", label: "Lunes" },
      { value: "martes", label: "Martes" },
      { value: "miercoles", label: "Miércoles" },
      { value: "jueves", label: "Jueves" },
      { value: "viernes", label: "Viernes" },
      { value: "sabado", label: "Sábado" },
      { value: "domingo", label: "Domingo" },
    ],
    []
  );

  const toEmailOptions = useCallback(
    (values = []) => {
      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byEmail = new Map(
        (users || []).map((u) => [String(u.email || "").toLowerCase(), u])
      );

      return values
        .map((v) => {
          const str = String(v ?? "");
          const isNumeric = typeof v === "number" || /^[0-9]+$/.test(str);

          if (isNumeric) {
            const u = byId.get(Number(v));
            return u?.email ? { value: u.id, label: u.email } : null;
          }

          const email = str.trim().toLowerCase();
          const u = byEmail.get(email);
          if (u) return { value: u.id, label: u.email };

          return email ? { value: email, label: String(v) } : null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const toPhoneOptions = useCallback(
    (values = []) => {
      const byId = new Map((users || []).map((u) => [Number(u.id), u]));
      const byPhone = new Map((users || []).map((u) => [String(u.phone), u]));

      return values
        .map((v) => {
          const str = String(v ?? "");
          const isNumeric = typeof v === "number" || /^[0-9]+$/.test(str);

          if (isNumeric) {
            const u = byId.get(Number(v));
            return u?.phone ? { value: u.id, label: String(u.phone) } : null;
          }

          const u = byPhone.get(str);
          if (u) return { value: u.id, label: String(u.phone) };

          return str ? { value: str, label: str } : null;
        })
        .filter(Boolean);
    },
    [users]
  );

  const toDeviceOptions = useCallback(
    (deviceIds = []) => {
      const byId = new Map((devices || []).map((d) => [d.value, d]));

      return deviceIds.map((d) => {
        const id = d?.device_id ?? d?.id ?? d;
        return (
          byId.get(id) || {
            value: id,
            label: d?.device?.name
              ? `${d.device.name} (${d.device.serial_number})`
              : String(id),
          }
        );
      });
    },
    [devices]
  );

  const buildStateFromInitial = useCallback(() => {
    const f = initialData?.config?.frequency;

    return {
      name: initialData?.name || "",
      service: initialData?.config?.service || "electricidad",
      interval: initialData?.config?.interval || "d",
      operator: initialData?.config?.operator || ">",
      value:
        initialData?.config?.value !== undefined &&
        initialData?.config?.value !== null
          ? String(initialData.config.value)
          : "",
      unitCost:
        initialData?.config?.unitCost !== undefined &&
        initialData?.config?.unitCost !== null
          ? String(initialData.config.unitCost)
          : "",
      unit: initialData?.config?.unit || null,
      active: initialData?.active === 1 || initialData?.active === true,
      devices: toDeviceOptions(initialData?.config?.devices || []),
      days: initialData?.config?.days || { from: "lunes", to: "domingo" },
      emails: toEmailOptions(f?.realtime?.emails || []),
      phones: toPhoneOptions(f?.realtime?.phones || []),
    };
  }, [initialData, toDeviceOptions, toEmailOptions, toPhoneOptions]);

  const [formData, setFormData] = useState(() => buildStateFromInitial());
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const lastInitKeyRef = useRef(null);
  const didHydrateRef = useRef(false);
  const lastPreviewKeyRef = useRef("");

  const initKey = useMemo(
    () =>
      initialData?.id ??
      initialData?.uuid ??
      JSON.stringify({
        name: initialData?.name,
        active: initialData?.active,
        service: initialData?.config?.service,
        interval: initialData?.config?.interval,
        operator: initialData?.config?.operator,
        value: initialData?.config?.value,
        unit: initialData?.config?.unit,
        unitCost: initialData?.config?.unitCost,
        devices: (initialData?.config?.devices || []).map(
          (d) => d?.device_id ?? d?.id ?? d
        ),
        days: initialData?.config?.days,
        emails: initialData?.config?.frequency?.realtime?.emails || [],
        phones: initialData?.config?.frequency?.realtime?.phones || [],
      }),
    [initialData]
  );

  useEffect(() => {
    if (!initialData || !users?.length || !devices?.length) return;

    const sameInit = lastInitKeyRef.current === initKey;
    if (isDirty && sameInit) return;

    const next = buildStateFromInitial();

    setFormData(next);
    lastInitKeyRef.current = initKey;
    didHydrateRef.current = true;
    setIsDirty(false);

    const firstDeviceId = next?.devices?.[0]?.value;
    const service = next?.service || "";
    lastPreviewKeyRef.current = firstDeviceId ? `${firstDeviceId}__${service}` : "";
  }, [initKey, initialData, users, devices, buildStateFromInitial, isDirty]);

  const handleChange = (field, value) => {
    setIsDirty(true);

    setFormData((prev) => {
      if (field === "devices") {
        const nextDevices = value || [];
        const prevFirstId = prev.devices?.[0]?.value;
        const nextFirstId = nextDevices?.[0]?.value;
        const changedFirstDevice = prevFirstId !== nextFirstId;

        return {
          ...prev,
          devices: nextDevices,
          ...(changedFirstDevice
            ? {
                unit: null,
                unitCost: "",
                emails: [],
                phones: [],
              }
            : {}),
        };
      }

      if (field === "service") {
        return {
          ...prev,
          service: value,
          unit: null,
          unitCost: "",
        };
      }

      return { ...prev, [field]: value };
    });
  };

  useEffect(() => {
    const loadUnit = async () => {
      const firstDeviceId = formData.devices?.[0]?.value;
      const service = formData.service;

      if (!firstDeviceId) {
        setFormData((prev) => ({ ...prev, unit: null, unitCost: "" }));
        lastPreviewKeyRef.current = "";
        return;
      }

      const previewKey = `${firstDeviceId}__${service}`;

      if (!didHydrateRef.current) return;

      if (lastPreviewKeyRef.current === previewKey) return;

      try {
        setLoadingUnit(true);

        const data = await getCostUnitPreview(firstDeviceId, service, token);

        setFormData((prev) => ({
          ...prev,
          unit: data?.unit || null,
          unitCost:
            data?.unitCost !== undefined && data?.unitCost !== null
              ? String(data.unitCost)
              : "",
        }));

        lastPreviewKeyRef.current = previewKey;
      } catch (error) {
        console.error("Error cargando unidad:", error);
        setFormData((prev) => ({ ...prev, unit: null, unitCost: "" }));
        Toast.fire({
          icon: "warning",
          title: "No se pudo cargar la unidad de medida: " + error.message,
        });
      } finally {
        setLoadingUnit(false);
      }
    };

    loadUnit();
  }, [formData.devices, formData.service, token]);

  const filteredUsers = useFilteredUsersByDevice(
    formData.devices,
    users,
    userPrivilege
  );

  const emailOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u?.email)
        .map((u) => ({ value: u.id, label: u.email })),
    [filteredUsers]
  );

  const phoneOptions = useMemo(
    () =>
      (filteredUsers || [])
        .filter((u) => !!u?.phone)
        .map((u) => ({ value: u.id, label: String(u.phone) })),
    [filteredUsers]
  );

  const selectedBranchId = formData.devices?.[0]?.data?.branch_fk;

  const filteredDevices = useMemo(() => {
    return selectedBranchId
      ? devices.filter((d) => d.data?.branch_fk === selectedBranchId)
      : devices;
  }, [selectedBranchId, devices]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }
    if (!formData.devices?.length) {
      newErrors.devices = "Selecciona al menos un dispositivo";
    }
    if (!formData.service) {
      newErrors.service = "El servicio es obligatorio";
    }
    if (!formData.interval) {
      newErrors.interval = "El intervalo es obligatorio";
    }
    if (!formData.operator) {
      newErrors.operator = "El operador es obligatorio";
    }
    if (!formData.value || Number(formData.value) <= 0) {
      newErrors.value = "Debes ingresar un valor mayor a 0";
    }
    if (!formData.unitCost || Number(formData.unitCost) <= 0) {
      newErrors.unitCost = "Debes ingresar el costo por unidad";
    }
    if (!formData.unit) {
      newErrors.unit = "La unidad es obligatoria";
    }

    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  }, [formData]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const handleSubmit = () => {
    Swal.fire({
      title: "¿Desea guardar los cambios de la alerta?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.formatMessage({
        id: "confirm.yes",
        defaultMessage: "Sí, continuar.",
      }),
      cancelButtonText: intl.formatMessage({
        id: "confirm.no",
        defaultMessage: "Cancelar",
      }),
    }).then((result) => {
      if (result.isConfirmed && isValid) {
        const payload = {
          type: "COSTE",
          name: formData.name?.toUpperCase(),
          active: formData.active ? 1 : 0,
          config: {
            service: formData.service,
            interval: formData.interval,
            operator: formData.operator,
            value: formData.value,
            unit: formData.unit,
            unitCost: formData.unitCost,
            devices: formData.devices.map((d) => d.value),
            days: formData.days,
            frequency: {
              realtime: {
                enabled:
                  (formData.emails?.length || 0) > 0 ||
                  (formData.phones?.length || 0) > 0,
                rateLimit: `1${formData.interval}`,
                emails: formData.emails.map((e) => e.value),
                phones: formData.phones.map((p) => p.value),
              },
              daily: { enabled: false, time: null, emails: [], phones: [] },
              weekly: {
                enabled: false,
                day: null,
                time: null,
                emails: [],
                phones: [],
              },
              monthly: {
                enabled: false,
                day: null,
                time: null,
                emails: [],
                phones: [],
              },
            },
          },
        };

        onSave(payload);
      } else if (!isValid) {
        Toast.fire({
          icon: "info",
          title: intl.formatMessage(
            {
              id: "error.saveAlert",
              defaultMessage: "No se pudo guardar la alerta: {errors}",
            },
            { errors: Object.values(errors).join(", ") }
          ),
        });
      }
    });
  };

  return (
    <div className="coste-form">
      <h3 className="coste-title">Editar alerta: Coste</h3>

      <label className="coste-label">Nombre de la alerta</label>
      <input
        type="text"
        className="coste-input"
        value={formData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        placeholder="Ej: Coste eléctrico mensual"
      />
      {errors.name && <small className="error-text">{errors.name}</small>}

      <label className="coste-label">Condición</label>
      <div className="coste-inline">
        <Select
          options={services}
          value={services.find((s) => s.value === formData.service)}
          onChange={(val) => handleChange("service", val.value)}
          className="coste-select small"
        />
        {errors.service && (
          <small className="error-text">{errors.service}</small>
        )}

        <Select
          options={intervals}
          value={intervals.find((i) => i.value === formData.interval)}
          onChange={(val) => handleChange("interval", val.value)}
          className="coste-select tiny"
        />
        {errors.interval && (
          <small className="error-text">{errors.interval}</small>
        )}

        <Select
          options={operators}
          value={operators.find((o) => o.value === formData.operator)}
          onChange={(val) => handleChange("operator", val.value)}
          className="coste-select tiny"
        />
        {errors.operator && (
          <small className="error-text">{errors.operator}</small>
        )}

        <input
          type="number"
          className="coste-input small"
          value={formData.value}
          onChange={(e) => handleChange("value", e.target.value)}
          placeholder="Valor"
        />
        {errors.value && <small className="error-text">{errors.value}</small>}

        <span>{loadingUnit ? "Cargando..." : formData.unit || "-"}</span>
        {errors.unit && <small className="error-text">{errors.unit}</small>}
      </div>

      <label className="coste-label">
        Costo por unidad ({loadingUnit ? "Cargando..." : formData.unit || "-"})
      </label>
      <input
        type="number"
        className="coste-input small"
        value={formData.unitCost}
        onChange={(e) => handleChange("unitCost", e.target.value)}
        placeholder="Ej: 66.47"
      />
      {errors.unitCost && (
        <small className="error-text">{errors.unitCost}</small>
      )}

      <label className="coste-label">¿Activar?</label>
      <div className="coste-inline">
        <label>
          <input
            type="radio"
            checked={!!formData.active}
            onChange={() => handleChange("active", true)}
          />{" "}
          Sí
        </label>
        <label>
          <input
            type="radio"
            checked={!formData.active}
            onChange={() => handleChange("active", false)}
          />{" "}
          No
        </label>
      </div>

      <label className="coste-label">Aplica a</label>
      <Select
        isMulti
        options={filteredDevices}
        value={formData.devices}
        onChange={(val) => handleChange("devices", val)}
        className="coste-select"
        placeholder="Selecciona dispositivos"
      />
      {errors.devices && <small className="error-text">{errors.devices}</small>}

      <label className="coste-label">Días</label>
      <div className="coste-inline">
        Desde
        <Select
          options={daysOptions}
          value={daysOptions.find((d) => d.value === formData.days.from)}
          onChange={(val) =>
            handleChange("days", { ...formData.days, from: val.value })
          }
          className="coste-select small"
        />
        Hasta
        <Select
          options={daysOptions}
          value={daysOptions.find((d) => d.value === formData.days.to)}
          onChange={(val) =>
            handleChange("days", { ...formData.days, to: val.value })
          }
          className="coste-select small"
        />
      </div>

      <label className="coste-label">Avisos</label>
      <div className="coste-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.emails.length > 0}
            readOnly
          />{" "}
          Enviar por email
        </label>
        <Select
          isMulti
          options={emailOptions}
          value={formData.emails}
          onChange={(val) => handleChange("emails", val)}
          className="coste-select"
          isDisabled={!formData.devices || formData.devices.length === 0}
          placeholder="Selecciona emails"
        />
      </div>

      {/* <div className="coste-inline">
        <label>
          <input
            type="checkbox"
            checked={formData.phones.length > 0}
            readOnly
          />{" "}
          Enviar por SMS
        </label>
        <Select
          isMulti
          options={phoneOptions}
          value={formData.phones}
          onChange={(val) => handleChange("phones", val)}
          className="coste-select"
          isDisabled={!formData.devices || formData.devices.length === 0}
          placeholder="Selecciona teléfonos"
        />
      </div> */}

      <div className="coste-actions">
        <button
          className="coste-btn-save"
          onClick={handleSubmit}
          disabled={!isValid}
        >
          Guardar
        </button>
        <button className="coste-btn-cancel" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default CostAlertFormEdit;